// src/hooks/useLMSAuth.js
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { Navigate } from 'react-router-dom';

const LMSAuthContext = createContext(null);

export function LMSAuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [account,     setAccount]     = useState(null);
  const [institution, setInstitution] = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadAccount(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) await loadAccount(session.user.id);
        else {
          setUser(null); setAccount(null);
          setInstitution(null); setLoading(false);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  const loadAccount = async (userId) => {
    try {
      const { data } = await supabase
        .from('institution_accounts')
        .select('*, institutions(id,name,city,country,category,type)')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (data) {
        setAccount(data);
        setInstitution(data.institutions);
      }
    } catch (err) {
      console.error('LMS loadAccount error:', err);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setAccount(null); setInstitution(null);
  };

  const refreshAccount = async () => {
    if (!user) return;
    await loadAccount(user.id);
  };

  return (
    <LMSAuthContext.Provider value={{
      user, account, institution,
      loading, signOut, refreshAccount,
    }}>
      {children}
    </LMSAuthContext.Provider>
  );
}

const _useLMSAuthBase = () => useContext(LMSAuthContext);

export const useLMSAuth = () => {
  const ctx = _useLMSAuthBase();
  // If admin institution is null (staff login), get institution_id from staff session
  if (ctx && !ctx.institution) {
    try {
      const s = localStorage.getItem('lms_staff_session');
      if (s) {
        const sess = JSON.parse(s);
        if (sess?.institution_id) {
          // Return a synthetic institution object so all pages work
          return {
            ...ctx,
            institution: { id: sess.institution_id, name: '' },
          };
        }
      }
      // Also try the stored institution_id
      const instId = localStorage.getItem('lms_institution_id');
      if (instId) {
        return {
          ...ctx,
          institution: { id: instId, name: '' },
        };
      }
    } catch(_) {}
  }
  return ctx;
};

// ── LMS Protected Route ───────────────────────────────
// Add this to the same useLMSAuth.js file
// Import Navigate from react-router-dom at the top of the file


export function LMSProtectedRoute({ children }) {
  const { user, account, loading } = useLMSAuth();

  if (loading) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F8FAFF',
    }}>
      <div style={{
        width: 36, height: 36,
        border: '4px solid #E2E8F0',
        borderTopColor: '#4F8EF7',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );

  // Allow staff session through — staff login with PIN, no Supabase auth user
  const hasStaffSession = (() => {
    try {
      const s = localStorage.getItem('lms_staff_session');
      if (!s) return false;
      const sess = JSON.parse(s);
      return !!(sess?.staff_id);
    } catch(_) { return false; }
  })();

  if (!user && !account && !hasStaffSession) return <Navigate to="/lms/login" replace />;

  // Staff session — no account object, skip admin checks
  if (hasStaffSession && !account) return <>{children}</>;

  if (!account.is_active || account.verification_status !== 'approved') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8FAFF',
        padding: 20,
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 40,
          maxWidth: 420,
          textAlign: 'center',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>⏳</div>
          <h2 style={{ fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
            Pending Approval
          </h2>
          <p style={{ color: '#6B7280', fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
            Your institution account is pending admin approval.
            You will receive your login credentials via email once approved.
          </p>
          <p style={{ color: '#9CA3AF', fontSize: 12, margin: 0 }}>
            Contact{' '}
            <a href="mailto:admin@inncatechnologies.com"
              style={{ color: '#4F8EF7' }}>
              admin@inncatechnologies.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return children;
}